import type { Feuille } from '../partage/api/types';
import { IconeHorloge } from '../partage/icones';
import { dureeLisible, jourAbrege } from '../partage/datesEnMots';
import {
  AUCUNE_DUREE_REGLEE,
  AVANCEMENT,
  compteARebours,
  ECHEANCE,
  ECHEANCES_QUI_APPROCHENT,
  MOTS_DES_ETATS_D_ANALYSE,
  MOTS_DES_REGROUPEMENTS,
  nAutres,
  nBullesDuGroupe,
  OU_PASSE_LE_TEMPS,
  ouPasseLeTempsPar,
  parRegroupement,
  PANNEAU_D_ANALYSE,
  REGROUPER_PAR,
  RENDEZ_VOUS,
  RESTANTES,
  RIEN_DE_DATE_A_VENIR,
  SANS_DUREE,
  surLeTotal,
} from '../partage/vocabulaire';
import type { GroupeALEcran, Regroupement } from './groupesALEcran';
import {
  barreDeLAvancement,
  enPeu,
  ouPasseLeTemps,
  repartitionDessinee,
  tempsCompare,
  type BarreDeLAvancement,
} from './lignesDuPanneauDAnalyse';
import './PanneauDAnalyse.css';

/** Les choix de « Regrouper par », dans l'ordre où G les fait tourner. */
const CHOIX_DU_REGROUPEMENT = ['rien', 'etat', 'contact', 'lieu', 'mois'] as const;

/** La barre de l'avancement : un segment par bulle comptée, ou une seule barre au-delà. */
function Barre({ barre, petite = false }: { barre: BarreDeLAvancement; petite?: boolean }) {
  const classe = petite ? ' analyse-barre--petite' : '';
  return barre.sorte === 'segments' ? (
    <span className={`analyse-segments${classe}`}>
      {barre.pleins.map((plein, i) => (
        <i key={i} className={plein ? 'analyse-segment--plein' : undefined} />
      ))}
    </span>
  ) : (
    <span className={`analyse-barre${classe}`}>
      <i style={{ width: `${barre.part * 100}%` }} />
    </span>
  );
}

/**
 * **Le panneau d'analyse** (chantier theme-analyse, ticket 02 ; maquette `quatre-themes.html`,
 * `panneauAnalyse`, `.pan-a`), à gauche de la Carte et de la Hiérarchie en Analyse, à la place de
 * « Comment j'avance » (décision d'Igor du 2026-10-21, choix A). Il lit les comptes du serveur
 * (`Feuille.analyse`), rangés par `lignesDuPanneauDAnalyse.ts` : rien n'est compté ici.
 *
 * Sans regroupement : l'avancement (le « N sur M » du centre), le temps qui reste, la répartition
 * par état, les échéances qui approchent en J-n, où passe le temps par étape, « Regrouper par ».
 * Pas de route : l'app ne connaît pas le temps de trajet (choix de Claude du cadrage). Le survol
 * d'une ligne allume sa bulle ; la bulle allumée (un clic sur la carte) allume ses lignes.
 */
export function PanneauDAnalyse({
  feuille,
  surlignee,
  onSurligner,
  regroupement = null,
  groupes = null,
  onRegrouper = null,
  groupeVu = null,
  onSurlignerLeGroupe,
}: {
  feuille: Feuille;
  surlignee: string | null;
  onSurligner: (id: string) => void;
  /** Le regroupement de la Carte (ticket 03) et ses groupes : le panneau suit ; `null` : aucun. */
  regroupement?: Regroupement | null;
  groupes?: readonly GroupeALEcran[] | null;
  /** « Regrouper par » ; `null` : la Hiérarchie, qui ne regroupe pas (ses choix restent grisés). */
  onRegrouper?: ((par: Regroupement | null) => void) | null;
  /** Le bloc de groupe survolé : ses bulles sont allumées sur la carte. */
  groupeVu?: string | null;
  onSurlignerLeGroupe?: (cle: string, bulles: readonly string[]) => void;
}) {
  const choix = (
    <>
      <h4>{REGROUPER_PAR}</h4>
      <div className="analyse-regrouper">
        {CHOIX_DU_REGROUPEMENT.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={c === (regroupement ?? 'rien')}
            disabled={!onRegrouper && c !== 'rien'}
            onClick={() => onRegrouper?.(c === 'rien' ? null : c)}
          >
            {MOTS_DES_REGROUPEMENTS[c]}
          </button>
        ))}
      </div>
    </>
  );

  if (regroupement && groupes) {
    return (
      <aside className="marge panneau-analyse" aria-label={PANNEAU_D_ANALYSE} data-marge="analyse">
        <BlocsDesGroupes
          par={regroupement}
          groupes={groupes}
          surlignee={surlignee}
          groupeVu={groupeVu}
          onSurligner={onSurligner}
          onSurlignerLeGroupe={onSurlignerLeGroupe}
        />
        {choix}
      </aside>
    );
  }

  const analyse = feuille.analyse;
  const { faites, total } = analyse.avancement;
  const barre = barreDeLAvancement(analyse.avancement);
  const repartition = repartitionDessinee(analyse);
  const echeances = enPeu(analyse.echeances);
  const temps = enPeu(ouPasseLeTemps(analyse.tempsParEtape));
  const vue = (id: string) => (id === surlignee ? ' analyse-ligne--vue' : '');

  return (
    <aside className="marge panneau-analyse" aria-label={PANNEAU_D_ANALYSE} data-marge="analyse">
      <h4>{AVANCEMENT}</h4>
      <Barre barre={barre} />
      <div className="analyse-chiffres">
        <span>
          <b>{faites}</b>
          {surLeTotal(total)}
        </span>
        <span>
          <b>{dureeLisible(analyse.tempsQuiReste)}</b>
          {RESTANTES}
        </span>
        {analyse.sansDuree > 0 && (
          <span>
            <b>{analyse.sansDuree}</b>
            {SANS_DUREE}
          </span>
        )}
      </div>
      {repartition.length > 0 && (
        <>
          <div className="analyse-repartition">
            {repartition.map((r) => (
              <i key={r.etat} className={`analyse-etat--${r.etat}`} style={{ flex: r.nombre }} />
            ))}
          </div>
          <div className="analyse-legende">
            {repartition.map((r) => (
              <span key={r.etat}>
                <i className={`analyse-etat--${r.etat}`} />
                {MOTS_DES_ETATS_D_ANALYSE[r.etat]} {r.nombre}
              </span>
            ))}
          </div>
        </>
      )}

      <h4>{ECHEANCES_QUI_APPROCHENT}</h4>
      {echeances.montrees.length === 0 && <p className="analyse-note">{RIEN_DE_DATE_A_VENIR}</p>}
      {echeances.montrees.map((e) => (
        <div
          key={e.id}
          className={`analyse-echeance${e.lueur ? ` analyse-echeance--${e.lueur}` : ''}${vue(e.id)}`}
          data-surligne={e.id}
          onMouseEnter={() => onSurligner(e.id)}
        >
          <span className="analyse-rebours">{compteARebours(e.dans)}</span>
          <span>
            <b>{e.titre}</b>
            <br />
            {e.sorte === 'rendez_vous' ? RENDEZ_VOUS : ECHEANCE}, {jourAbrege(e.jour)}
          </span>
        </div>
      ))}
      {echeances.autres > 0 && <p className="analyse-note">{nAutres(echeances.autres)}</p>}

      <h4>{OU_PASSE_LE_TEMPS}</h4>
      {temps.montrees.length === 0 && <p className="analyse-note">{AUCUNE_DUREE_REGLEE}</p>}
      {temps.montrees.map((l) => (
        <div
          key={l.id}
          className={`analyse-temps${l.faite ? ' analyse-temps--faite' : ''}${vue(l.id)}`}
          data-surligne={l.id}
          onMouseEnter={() => onSurligner(l.id)}
        >
          <span className="analyse-temps-titre">{l.titre}</span>
          <span className="analyse-temps-barre">
            <i style={{ width: `${l.longueur * 100}%` }} />
          </span>
          <span className="analyse-temps-duree">{dureeLisible(l.minutes)}</span>
        </div>
      ))}
      {temps.autres > 0 && <p className="analyse-note">{nAutres(temps.autres)}</p>}

      {choix}
    </aside>
  );
}

/**
 * Le panneau quand on regroupe (maquette `panneauAnalyse`, `groupe !== 'rien'`, `.gc`, `.cmp`) : un
 * bloc par groupe, dans l'ordre des colonnes (son nom, ses bulles, sa barre, « f sur n · durée », sa
 * plus proche date en J-n, le numéro par contact), puis le temps comparé entre groupes. Les comptes
 * sont ceux du serveur (`useGroupes`), nommés par `groupesALEcran` : rien n'est compté ici.
 */
function BlocsDesGroupes({
  par,
  groupes,
  surlignee,
  groupeVu,
  onSurligner,
  onSurlignerLeGroupe,
}: {
  par: Regroupement;
  groupes: readonly GroupeALEcran[];
  surlignee: string | null;
  groupeVu: string | null;
  onSurligner: (id: string) => void;
  onSurlignerLeGroupe?: (cle: string, bulles: readonly string[]) => void;
}) {
  const vu = (g: GroupeALEcran | undefined) => !!g && (g.cle === groupeVu || (surlignee !== null && g.bulles.some((b) => b.id === surlignee)));
  const survoler = (g: GroupeALEcran | undefined) => () => g && onSurlignerLeGroupe?.(g.cle, g.bulles.map((b) => b.id));
  return (
    <>
      <h4>{parRegroupement(par)}</h4>
      {groupes.map((g) => (
        <div
          key={g.cle}
          className={`analyse-groupe${vu(g) ? ' analyse-ligne--vue' : ''}`}
          data-groupe={g.cle}
          onMouseEnter={survoler(g)}
        >
          <div className="analyse-groupe-titre">
            <b>{g.nom}</b>
            <span>{nBullesDuGroupe(g.bulles.length)}</span>
          </div>
          {g.numero && <div className="analyse-groupe-ligne analyse-groupe-numero">{g.numero}</div>}
          {/* Un groupe d'idées seules ne compte rien (les idées sont hors du compte) : ni barre ni « 0 sur 0 ». */}
          {g.compte.total > 0 && <Barre barre={barreDeLAvancement(g.compte)} petite />}
          <div className="analyse-groupe-ligne analyse-groupe-ligne--douce">
            {g.compte.total > 0 && `${g.compte.faites} ${surLeTotal(g.compte.total)} · `}
            <IconeHorloge className="analyse-icone" />
            {dureeLisible(g.duree)}
          </div>
          {g.prochaine && (
            <div className="analyse-groupe-ligne">
              <span
                className={`analyse-rebours-mini${g.prochaine.lueur ? ` analyse-rebours-mini--${g.prochaine.lueur}` : ''}`}
              >
                {compteARebours(g.prochaine.dans)}
              </span>
              {g.prochaine.titre}
            </div>
          )}
          <div className="analyse-groupe-bulles">
            {g.bulles.map((b) => (
              <span
                key={b.id}
                data-surligne={b.id}
                className={
                  [b.etat === 'fait' ? 'analyse-bulle--faite' : '', b.id === surlignee ? 'analyse-bulle--vue' : '']
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  onSurligner(b.id);
                }}
              >
                {b.titre}
              </span>
            ))}
          </div>
        </div>
      ))}

      <h4>{ouPasseLeTempsPar(par)}</h4>
      {tempsCompare(groupes).map((t) => (
        <div
          key={t.cle}
          className={`analyse-temps${vu(groupes.find((g) => g.cle === t.cle)) ? ' analyse-ligne--vue' : ''}`}
          data-groupe={t.cle}
          onMouseEnter={survoler(groupes.find((g) => g.cle === t.cle))}
        >
          <span className="analyse-temps-titre">{t.nom}</span>
          <span className="analyse-temps-barre">
            <i style={{ width: `${t.longueur * 100}%` }} />
          </span>
          <span className="analyse-temps-duree">{dureeLisible(t.minutes)}</span>
        </div>
      ))}
    </>
  );
}
